// Outfit selections are stored inside the existing `outfits: string[]` array.
// Each entry is encoded as `${nome}${SEP}${genero}${SEP}${addons}` so we can
// remember the chosen gender and which add-ons (1, 2, or both) were selected.
// Legacy entries without the separator are treated as a base outfit.

export const OUTFIT_SEP = "::#::";

export interface OutfitImages {
  img_outfit?: string | null;
  img_addon1?: string | null;
  img_addon2?: string | null;
  img_completo?: string | null;
}

export interface OutfitSelection {
  nome: string;
  genero: string;
  addons: string; // "", "1", "2" or "12"
}

export const encodeOutfit = (sel: OutfitSelection): string =>
  [sel.nome, sel.genero || "", sel.addons || ""].join(OUTFIT_SEP);

export const decodeOutfit = (raw: string): OutfitSelection => {
  if (raw.includes(OUTFIT_SEP)) {
    const parts = raw.split(OUTFIT_SEP);
    return { nome: parts[0], genero: parts[1] || "", addons: parts[2] || "" };
  }
  return { nome: raw, genero: "", addons: "" };
};

export const hasAddon = (addons: string, n: 1 | 2): boolean => addons.includes(String(n));

export const toggleAddon = (addons: string, n: 1 | 2): string => {
  const a1 = hasAddon(addons, 1);
  const a2 = hasAddon(addons, 2);
  const next1 = n === 1 ? !a1 : a1;
  const next2 = n === 2 ? !a2 : a2;
  return `${next1 ? "1" : ""}${next2 ? "2" : ""}`;
};

// Resolve the single image that represents the selected outfit:
// no add-ons -> base, one add-on -> that add-on, both -> the full outfit.
export const pickOutfitImage = (rec: OutfitImages | undefined | null, addons: string): string | null => {
  if (!rec) return null;
  const a1 = hasAddon(addons, 1);
  const a2 = hasAddon(addons, 2);
  if (a1 && a2) return rec.img_completo ?? rec.img_outfit ?? null;
  if (a1) return rec.img_addon1 ?? rec.img_outfit ?? null;
  if (a2) return rec.img_addon2 ?? rec.img_outfit ?? null;
  return rec.img_outfit ?? null;
};

export const outfitGenderSymbol = (genero: string): "M" | "F" | null => {
  const g = (genero || "").toLowerCase();
  if (g.startsWith("masc") || g.startsWith("mal")) return "M";
  if (g.startsWith("fem")) return "F";
  return null;
};

// Short badge for the selected addon condition: "Completo", "Addon 1", "Addon 2" or null.
export const outfitAddonBadge = (addons: string): "Completo" | "Addon 1" | "Addon 2" | null => {
  const a1 = hasAddon(addons, 1);
  const a2 = hasAddon(addons, 2);
  if (a1 && a2) return "Completo";
  if (a1) return "Addon 1";
  if (a2) return "Addon 2";
  return null;
};

// Same, but accepts the encoded raw outfit entry.
export const outfitAddonBadgeFromRaw = (raw: string): "Completo" | "Addon 1" | "Addon 2" | null =>
  outfitAddonBadge(decodeOutfit(raw).addons);

// Human-readable label, e.g. "Demon Hunter (Completo)".
export const outfitLabel = (raw: string): string => {
  const { nome, addons } = decodeOutfit(raw);
  const a1 = hasAddon(addons, 1);
  const a2 = hasAddon(addons, 2);
  if (a1 && a2) return `${nome} (Completo)`;
  if (a1) return `${nome} (Addon 1)`;
  if (a2) return `${nome} (Addon 2)`;
  return nome;
};
